import { faPause, faPenLine, faPencilSlash } from '@fortawesome/pro-solid-svg-icons';

import { useTranslate } from '@growtherapy/i18n';
import { Icon, Tag, Tooltip } from '@growtherapy/sprout-ui';

import { Translate } from '../i18n/Translate';
import { ConsentStatus } from '../video-sdk/core/message/constants';

type TranscriptionPillProps = {
  isRecording: boolean;
  patientConsent: ConsentStatus;
};

function TranscriptionPillTag({ isRecording, patientConsent }: TranscriptionPillProps) {
  const t = useTranslate();

  if (isRecording) {
    return (
      <Tag className='bg-neutral-900 text-neutral-600 gap-1.5 rebrand:gap-x-1.5 flex items-center rebrand:bg-surface-default rebrand:text-text-default'>
        <Icon aria-hidden icon={faPenLine} className='text-lilac-500 rebrand:text-text-default' />
        <span aria-hidden>{t('telehealth-core:recording.transcribing')}</span>
        {/* For live announcement */}
        <span className='sr-only normal-case'>{t('telehealth-core:recording.transcriptionInProgress')}</span>
      </Tag>
    );
  }

  if (
    patientConsent === ConsentStatus.UNDECIDED ||
    patientConsent === ConsentStatus.SOFT_NO ||
    patientConsent === ConsentStatus.DECIDING
  ) {
    return (
      <Tooltip
        childIsInteractive={false}
        className='bg-neutral-900 rebrand:bg-surface-inverse'
        text={
          <div className='max-w-[80vw] sm:max-w-[24.5rem]'>
            <Translate
              i18nKey='telehealth-core:recording.toTranscribeThisSessionPressThe'
              components={{
                1: <span className='sr-only' />,
                2: <Icon aria-hidden icon={faPenLine} />,
              }}
            />
          </div>
        }
      >
        <Tag className='bg-neutral-900 text-neutral-600 gap-1.5 rebrand:gap-x-1.5 flex items-center rebrand:bg-surface-default rebrand:text-text-default'>
          <Icon aria-hidden icon={faPencilSlash} className='rebrand:text-text-default' />
          <span aria-hidden>{t('telehealth-core:recording.notTranscribing')}</span>
          {/* For live announcement */}
          <span className='sr-only normal-case'>{t('telehealth-core:recording.theSessionIsNotBeingTranscribed')}</span>
        </Tag>
      </Tooltip>
    );
  }

  if (patientConsent === ConsentStatus.UNKNOWN) {
    return null;
  }

  return (
    <Tag className='bg-neutral-900 text-neutral-600 gap-1.5 rebrand:gap-x-1.5 flex items-center rebrand:bg-surface-default rebrand:text-text-default'>
      <Icon aria-hidden icon={faPause} className='text-lilac-500 rebrand:text-text-default' />
      <span aria-hidden>{t('telehealth-core:recording.transcriptionPaused')}</span>
      {/* For live announcement */}
      <span className='sr-only normal-case'>{t('telehealth-core:recording.transcriptionIsPausedForThisSession')}</span>
    </Tag>
  );
}

export function TranscriptionPill({ isRecording, patientConsent }: TranscriptionPillProps) {
  return (
    <div aria-live='polite'>
      <TranscriptionPillTag isRecording={isRecording} patientConsent={patientConsent} />
    </div>
  );
}
